import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Users, Handshake, TrendingUp, CheckCircle, Loader2 } from "lucide-react";
import { simpleContactService, type SimpleContact } from "@/services/contactService-simple";
import { partnerContactService, type PartnerContact } from "@/services/partnerContactService";
import { ContactsPipelineTable } from "./ContactsPipelineTable";
import { ContactDetailPage } from "./ContactDetailPage";
import { QuickAddBorrowerModal } from "./QuickAddBorrowerModal";
// Note: ContactModalRedesigned is no longer used - full-page detail view replaced it
import { QuickAddPartnerModal } from "./QuickAddPartnerModal";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TechnologyStatusBar } from "@/components/ui/TechnologyStatusBar";
import { toast } from "sonner";
import { supabase } from "@/utils/supabase";

interface SimpleContactsManagementProps {
  viewMode?: 'borrowers' | 'partners';
  onCreatePreApproval?: (prefilledData: {
    borrowerInfo: {
      salutation?: string;
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    };
    contactId: string;
  }) => void;
}

/**
 * Simplified Contact Management Component
 * Uses the new simplified contacts_simple table and service
 */
const SimpleContactsManagement: React.FC<SimpleContactsManagementProps> = ({ viewMode, onCreatePreApproval }) => {
  const [contacts, setContacts] = useState<SimpleContact[]>([]);
  const [partners, setPartners] = useState<PartnerContact[]>([]);
  const [contactsWithLoans, setContactsWithLoans] = useState<(SimpleContact & {
    loanHistory: unknown[];
    isPastCustomer: boolean;
    totalLoans: number;
    totalFundedAmount: number;
    lastLoanDate?: string;
  })[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    if (viewMode === 'partners') {
return 'referral-partners';
}
    return 'active-leads';
  });
  const [showQuickAddBorrower, setShowQuickAddBorrower] = useState(false);
  const [showQuickAddPartner, setShowQuickAddPartner] = useState(false);
  const [editingPartner, setEditingPartner] = useState<PartnerContact | null>(null);
  // Contact detail view state - when set, shows full-page detail instead of list
  const [detailViewContact, setDetailViewContact] = useState<SimpleContact | null>(null);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      if (viewMode === 'partners') {
        // Load partner contacts
        const partnersData = await partnerContactService.list();
        setPartners(partnersData);
      } else {
        // Load contacts with loan history for proper categorization
        const contactsWithHistory = await simpleContactService.listWithLoanHistory();
        setContactsWithLoans(contactsWithHistory);
        
        // Also load regular contacts for compatibility
        const contactsData = await simpleContactService.list();
        setContacts(contactsData);
      }
    } catch (error) {
      console.error('❌ Failed to load contacts:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to load contacts: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  }, [viewMode]);

  // Load contacts on mount
  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  // Update active tab when viewMode changes
  useEffect(() => {
    if (viewMode === 'partners') {
      setActiveTab('referral-partners');
    } else if (viewMode === 'borrowers') {
      setActiveTab('active-leads');
    }
  }, [viewMode]);

  const handleDeleteContact = async (contactId: string) => {
    try {
      if (viewMode === 'partners') {
        await partnerContactService.delete(contactId);
        toast.success('Partner deleted successfully');
      } else {
        await simpleContactService.softDelete(contactId);
        toast.success('Contact deleted successfully');
      }
      // Reload contacts to reflect the change
      await loadContacts();
    } catch (_error) {
      console.error('Failed to delete contact:', _error);
      toast.error('Failed to delete contact');
    }
  };

  const handleBulkDeleteContacts = async (contactIds: string[]) => {
    try {
      if (viewMode === 'partners') {
        await Promise.all(contactIds.map(id => partnerContactService.delete(id)));
        toast.success(`${contactIds.length} partners deleted successfully`);
      } else {
        await Promise.all(contactIds.map(id => simpleContactService.softDelete(id)));
        toast.success(`${contactIds.length} contacts deleted successfully`);
      }
      await loadContacts();
    } catch (_error) {
      console.error('Failed to delete contacts:', _error);
      toast.error('Failed to delete contacts');
    }
  };

  const handleBulkAssignContacts = async (contactIds: string[], officerId: string) => {
    try {
      // Call the bulk assign function
      const { data, error } = await supabase.rpc('bulk_assign_loan_officer', {
        p_contact_ids: contactIds,
        p_officer_id: officerId
      });
      
      if (error) {
        console.error('Supabase RPC error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
          fullError: error
        });
        throw error;
      }
      
      // Check the response from the function
      if (data && typeof data === 'object' && 'success' in data) {
        if (data.success) {
          toast.success(`Successfully assigned ${data.updated_count || contactIds.length} contact(s) to ${data.officer_name || 'loan officer'}`);
        } else {
          throw new Error(data.error || 'Assignment failed');
        }
      } else {
        toast.success(`Successfully assigned ${contactIds.length} contact(s) to loan officer`);
      }
      
      await loadContacts(); // Refresh the list
    } catch (error) {
      console.error('Failed to assign contacts:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to assign contacts to loan officer';
      toast.error(errorMessage);
      throw error; // Re-throw for the dialog to handle
    }
  };

  const handleCreateContact = (type: 'lead' | 'borrower' | 'referral_partner') => {
    // Open quick add modal based on type
    if (type === 'referral_partner') {
      setShowQuickAddPartner(true);
    } else {
      setShowQuickAddBorrower(true);
    }
  };

  const handleEditContact = (contactRow: { id: string; contactId?: string }) => {
    if (viewMode === 'partners') {
      // Find the partner from our loaded data
      const partner = partners.find(p => p.id === contactRow.id);
      if (partner) {
        // Use QuickAddPartnerModal for editing partners
        setEditingPartner(partner);
        setShowQuickAddPartner(true);
      }
    } else {
      // Find the actual SimpleContact from our loaded data
      const contact = contacts.find(c => c.id === contactRow.contactId || c.id === contactRow.id);
      if (contact) {
        // Open full-page detail view instead of modal
        setDetailViewContact(contact);
      }
    }
  };

  const handleDetailViewBack = () => {
    setDetailViewContact(null);
  };

  const handleContactSaved = async (_contactId: string) => {
    // Reload contacts to show the updated contact
    await loadContacts();
  };

  const handleContactDeleted = async (_contactId: string) => {
    // Reload contacts and return to list view
    await loadContacts();
    setDetailViewContact(null);
  };

  // Calculate statistics based on actual loan history
  const stats = viewMode === 'partners' ? {
    totalPartners: partners.length,
    activePartners: partners.filter(p => p.partnerStatus === 'active').length,
    referralPartners: partners.length,
    // Add these for type compatibility
    totalContacts: 0,
    activeLeads: 0,
    pastCustomers: 0
  } : {
    totalContacts: contacts.filter(c => !c.deletedAt).length,
    // Active Leads: Contacts with NO funded loans
    activeLeads: contactsWithLoans.filter(c => !c.deletedAt && !c.isPastCustomer && c.contactType !== 'referral_partner').length,
    // Past Customers: Contacts with at least one funded loan (closing_date exists)
    pastCustomers: contactsWithLoans.filter(c => !c.deletedAt && c.isPastCustomer).length,
    referralPartners: contacts.filter(c => !c.deletedAt && c.contactType === 'referral_partner').length,
    // Add these for type compatibility
    totalPartners: 0,
    activePartners: 0
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading contacts...</span>
      </div>
    );
  }

  // Show full-page detail view when a contact is selected
  if (detailViewContact && viewMode !== 'partners') {
    return (
      <ContactDetailPage
        contact={detailViewContact}
        onBack={handleDetailViewBack}
        onSaved={handleContactSaved}
        onDeleted={handleContactDeleted}
        onCreatePreApproval={onCreatePreApproval}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with integrated status - Move to absolute top */}
      <div className="flex justify-between items-center -mt-6 -mx-6 px-6 py-4 bg-background border-b mb-6">
        <TechnologyStatusBar pageTitle={
          viewMode === 'partners' ? 'Co-Marketing Partners' : 
          viewMode === 'borrowers' ? 'Borrowers & Leads' : 
          'Contact Management'
        } />
        <div className="flex space-x-2">
          <Button onClick={loadContacts} variant="outline">
            Refresh
          </Button>
          <Button onClick={() => handleCreateContact(viewMode === 'partners' ? 'referral_partner' : 'lead')}>
            <Plus className="h-4 w-4 mr-2" />
            Add Contact
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className={`grid grid-cols-1 ${viewMode === 'partners' ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-6`}>
        {viewMode !== 'partners' && (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Contacts</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.activeLeads + stats.pastCustomers}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Leads</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.activeLeads}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Past Customers</CardTitle>
                <CheckCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.pastCustomers}</div>
              </CardContent>
            </Card>
          </>
        )}

        {viewMode === 'partners' && (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Partners</CardTitle>
                <Handshake className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalPartners}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Partners</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.activePartners}</div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Contact Tabs - Only show for borrowers view */}
      {viewMode === 'partners' ? (
        // For partners view, show the table directly without tabs
        <div className="space-y-4">
          <ContactsPipelineTable
            simpleContacts={partners.map(p => ({
              id: p.id,
              contactType: 'referral_partner' as const,
              firstName: p.firstName,
              lastName: p.lastName,
              email: p.email || '',
              phone: p.phone || '',
              mailingStreet: p.businessStreet || '',
              mailingCity: p.businessCity || '',
              mailingState: p.businessState || '',
              mailingZip: p.businessZip || '',
              notes: p.notes,
              tags: p.tags,
              createdAt: p.createdAt,
              updatedAt: p.updatedAt
            }))}
            onDeleteContact={handleDeleteContact}
            onBulkDeleteContacts={handleBulkDeleteContacts}
            onBulkAssignContacts={handleBulkAssignContacts}
            onEditContact={handleEditContact}
            onRefresh={loadContacts}
            filterType="allContacts"
          />
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="active-leads">
              Active Leads
              <Badge variant="secondary" className="ml-2">
                {stats.activeLeads}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="past-customers">
              Past Customers
              <Badge variant="secondary" className="ml-2">
                {stats.pastCustomers}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="all-contacts">
              All Contacts
              <Badge variant="secondary" className="ml-2">
                {stats.activeLeads + stats.pastCustomers}
              </Badge>
            </TabsTrigger>
          </TabsList>

        <TabsContent value="active-leads" className="space-y-4">
          <ContactsPipelineTable
            simpleContacts={contactsWithLoans.filter(c => !c.deletedAt && !c.isPastCustomer && c.contactType !== 'referral_partner')}
            onDeleteContact={handleDeleteContact}
            onBulkDeleteContacts={handleBulkDeleteContacts}
            onBulkAssignContacts={handleBulkAssignContacts}
            onEditContact={handleEditContact}
            onRefresh={loadContacts}
            filterType="activeLeads"
            contactsWithLoans={contactsWithLoans}
          />
        </TabsContent>

        <TabsContent value="past-customers" className="space-y-4">
          <ContactsPipelineTable
            simpleContacts={contactsWithLoans.filter(c => !c.deletedAt && c.isPastCustomer)}
            onDeleteContact={handleDeleteContact}
            onBulkDeleteContacts={handleBulkDeleteContacts}
            onBulkAssignContacts={handleBulkAssignContacts}
            onEditContact={handleEditContact}
            onRefresh={loadContacts}
            filterType="pastCustomers"
            contactsWithLoans={contactsWithLoans}
          />
        </TabsContent>

        <TabsContent value="all-contacts" className="space-y-4">
          <ContactsPipelineTable
            simpleContacts={viewMode === 'borrowers' ? 
              contacts.filter(c => c.contactType === 'borrower' || c.contactType === 'lead') : 
              contacts
            }
            onDeleteContact={handleDeleteContact}
            onBulkDeleteContacts={handleBulkDeleteContacts}
            onBulkAssignContacts={handleBulkAssignContacts}
            onEditContact={handleEditContact}
            onRefresh={loadContacts}
            filterType="allContacts"
          />
        </TabsContent>
      </Tabs>
      )}

      {/* Quick Add Modals */}
      <QuickAddBorrowerModal
        open={showQuickAddBorrower}
        onClose={() => setShowQuickAddBorrower(false)}
        onSuccess={loadContacts}
      />

      <QuickAddPartnerModal
        open={showQuickAddPartner}
        onClose={() => {
          setShowQuickAddPartner(false);
          setEditingPartner(null);
        }}
        onSuccess={loadContacts}
        mode={editingPartner ? 'edit' : 'create'}
        initialPartner={editingPartner}
        partnerId={editingPartner?.id}
      />
    </div>
  );
};

export default SimpleContactsManagement;